import type { AppState, EditorGraphMode, RuntimeGraphKind } from '../state/appState.js';
import { createCommandHistory, type CommandHistory } from '../editor/commandHistory.js';
import { configureCommandReplay } from '../editor/commandReplay.js';
import { createEditorCommandExecutor } from '../editor/commandExecutor.js';
import { createEditorCommands, type EditorCommands } from '../editor/commands.js';
import { createEditorRuntime, type EditorRuntime } from '../editor/editorRuntime.js';
import {
  loadComponentSlotFor,
  loadLibraryItemFor,
  loadRuntimeGraphFor,
  setEditorGraphModeFor,
  syncEditorFor,
} from '../editor/editorSession.js';
import {
  captureEditorSessionFor,
  recoverEditorTargetFor,
  resetEditorAfterComponentRemovalFor,
} from '../editor/exec/session.js';
import { createGraphMutations, type GraphMutations } from '../editor/graphMutations.js';
import {
  cutEdgesWithHistoryFor,
  disconnectEdgeToPendingFor,
  type EditorConnectionMutations,
  reconnectFromInputFor,
  reconnectFromOutputFor,
  resolvePendingConnectionFor,
  type ConnectionMutationCommands,
} from '../editor/connectionMutations.js';
import { createHistoryStore, type HistoryStore } from '../editor/historyState.js';
import { createUserLibraryEntityId } from '../ids/entityIds.js';
import { nodeDefsForKind } from '../graph/nodeRegistry.js';

export interface EditorAppRuntime {
  appState: AppState;
  historyStore: HistoryStore;
  commandHistory: CommandHistory;
  editorRuntime: EditorRuntime;
  commands: EditorCommands;
  graphMutations: GraphMutations;
  connectionMutations: EditorConnectionMutations;
  loadComponentSlot(componentId: string | null, slot: 'geometry' | 'material', layerId?: string): boolean;
  loadLibraryItem(kind: 'geometry' | 'material', id: string): boolean;
  loadRuntimeGraph(kind: RuntimeGraphKind, id: string): boolean;
  setEditorGraphMode(mode: EditorGraphMode): void;
  syncEditor(): boolean;
}

export interface EditorAppRuntimeServices {
  historyStore?: HistoryStore;
  createUserLibraryId?: () => string;
  notifyEditorTargetSynced?: () => void;
}

export function createEditorAppRuntime(
  appState: AppState,
  services: EditorAppRuntimeServices = {},
): EditorAppRuntime {
  const historyStore = services.historyStore ?? createHistoryStore();
  const createUserLibraryId = services.createUserLibraryId ?? createUserLibraryEntityId;

  const syncEditor = () => syncEditorFor(appState);
  const loadComponentSlot = (componentId: string | null, slot: 'geometry' | 'material', layerId?: string) => (
    loadComponentSlotFor(appState, componentId, slot, layerId)
  );
  const loadLibraryItem = (kind: 'geometry' | 'material', id: string) => (
    loadLibraryItemFor(appState, kind, id, { syncEditor })
  );
  const loadRuntimeGraph = (kind: RuntimeGraphKind, id: string) => loadRuntimeGraphFor(appState, kind, id, { syncEditor });
  const resetEditorAfterComponentRemoval = () => {
    resetEditorAfterComponentRemovalFor(appState, { loadComponentSlot });
  };
  const recoverEditorTarget = () => {
    recoverEditorTargetFor(appState, { loadComponentSlot });
  };
  const captureEditorSession = (options: { sync?: boolean } = {}) => {
    if (options.sync !== false) syncEditor();
    return captureEditorSessionFor(appState);
  };

  const executor = createEditorCommandExecutor({
    clear: { captureEditorSession },
    sceneRemoval: {
      captureEditorSession,
      recoverEditorTarget,
      resetEditorAfterComponentRemoval,
    },
    componentSlot: {
      notifyEditorTargetSynced: services.notifyEditorTargetSynced,
      reloadComponentSlot: loadComponentSlot,
    },
    autoDetachComponentSlot: {
      notifyEditorTargetSynced: services.notifyEditorTargetSynced,
    },
    autoForkBuiltInLibrary: {
      captureEditorSession,
      createUserLibraryId,
      notifyEditorTargetSynced: services.notifyEditorTargetSynced,
    },
    editorSession: {
      loadComponentSlot,
      resetEditorAfterComponentRemoval,
    },
  });

  const editorRuntime = createEditorRuntime(appState, {
    executeCommand: executor.executeCommand,
    executeInternalAction: executor.executeInternalAction,
  });
  const commandHistory = createCommandHistory(historyStore);
  configureCommandReplay(commandHistory, editorRuntime);
  const commands = createEditorCommands(editorRuntime, { commandHistory, syncEditor });
  const graphMutations = createGraphMutations(commands);
  const connectionCommands: ConnectionMutationCommands = {
    connectEdge(edge, isReconnect) {
      const target = appState.nodes.find(node => node.id === edge.toNode);
      const source = appState.nodes.find(node => node.id === edge.fromNode);
      const defs = nodeDefsForKind(appState.editorGraphKind);
      const sourceDef = source ? defs[source.type] : undefined;
      const sourceOutputs = sourceDef
        ? (typeof sourceDef.outputs === 'function' ? sourceDef.outputs(source!) : sourceDef.outputs)
        : [];
      const sourceType = sourceOutputs[edge.fromPort]?.type;
      const shouldAdoptType = (
        appState.editorGraphKind === 'data'
        && target?.type === 'Output'
        && sourceType !== undefined
        && !appState.edges.some(candidate => (
          candidate.toNode === edge.toNode && candidate.toPort === edge.toPort
        ))
        && target.data.inputType !== sourceType
      );
      if (!shouldAdoptType) return commands.connectEdge(edge, isReconnect);
      return commandHistory.commandBatch.run('Connect Output', () => {
        commands.setNodeData(target.id, 'inputType', sourceType);
        commands.setNodeData(target.id, 'displayType', sourceType);
        return commands.connectEdge(edge, isReconnect);
      });
    },
    reconnectEdge: commands.reconnectEdge,
    disconnectEdges: commands.disconnectEdges,
  };

  return {
    appState,
    historyStore,
    commandHistory,
    editorRuntime,
    commands,
    graphMutations,
    connectionMutations: {
      resolvePendingConnection(nodeId, portIdx, io, pendingConn, clearPending) {
        return resolvePendingConnectionFor(
          appState,
          connectionCommands,
          nodeId,
          portIdx,
          io,
          pendingConn,
          clearPending,
        );
      },
      reconnectFromInput(nodeId, portIdx, getDotWorldPos) {
        return reconnectFromInputFor(appState, nodeId, portIdx, getDotWorldPos);
      },
      reconnectFromOutput(edge, getDotWorldPos) {
        return reconnectFromOutputFor(appState, edge, getDotWorldPos);
      },
      disconnectEdgeToPending(edge, clickPos, getDotWorldPos) {
        return disconnectEdgeToPendingFor(appState, edge, clickPos, getDotWorldPos);
      },
      cutEdgesWithHistory(edges) {
        return cutEdgesWithHistoryFor(connectionCommands, edges);
      },
    },
    loadComponentSlot,
    loadLibraryItem,
    loadRuntimeGraph,
    setEditorGraphMode(mode) {
      setEditorGraphModeFor(appState, mode, { syncEditor });
    },
    syncEditor,
  };
}
